import { unreadCount } from "../unread";
import { plainPreview } from "../preview";
import { useEffect, useState, type MutableRefObject } from "react";
import { db } from "../firebase";
import { PairingClient, type SessionContact, type TodoItem } from "../protocol";
import type { PairingPayload } from "../pairings";

/** How many recent messages each session listener keeps, so the sidebar can show an unread count. */
const UNREAD_SCAN = 20;

/**
 * The session registry + its listener-priority ladder. Subscribes each pairing's session contacts (splitting
 * archived out), then — once the structural list has painted (`secondaryReady`, flipped on idle) — attaches
 * the bulk per-session listeners that scale with the count: a sidebar preview each, an archived-unarchive
 * watcher, and the dashboard to-do rings (only while no chat is open). Deferring the bulk listeners past the
 * first paint stops a large session list from starving the structural subscriptions on load.
 */
export function useSessions(
  clients: MutableRefObject<Map<string, PairingClient>>,
  ready: boolean,
  pairings: PairingPayload[],
  selected: string | null,
): {
  sessions: SessionContact[];
  archivedSessions: SessionContact[];
  sessionsReady: boolean;
  /** Bumped each time a pairing's client lands in the map — see `clientsVersion` below. */
  clientsVersion: number;
  previews: Record<string, { text: string; ts: number }>;
  unreads: Record<string, number>;
  sessionTodos: Record<string, TodoItem[]>;
} {
  const [sessions, setSessions] = useState<SessionContact[]>([]);
  const [archivedSessions, setArchivedSessions] = useState<SessionContact[]>([]);
  const [sessionsReady, setSessionsReady] = useState(false);
  // The clients live in a REF, so a hook that subscribes through one has nothing reactive to wait on: on a
  // reload the selection is restored before `PairingClient.create` has been awaited, those effects run once
  // against an empty map, find no client, and never re-run — the surface sits on its skeleton until the
  // selection changes and re-triggers them. This counter is that missing signal.
  const [clientsVersion, setClientsVersion] = useState(0);
  const [secondaryReady, setSecondaryReady] = useState(false);
  const [previews, setPreviews] = useState<Record<string, { text: string; ts: number }>>({});
  const [unreads, setUnreads] = useState<Record<string, number>>({});
  const [sessionTodos, setSessionTodos] = useState<Record<string, TodoItem[]>>({});

  // Per pairing: a client + a live session-contacts subscription; merge across pairings.
  useEffect(() => {
    if (!ready) return;
    const unsubs: Array<() => void> = [];
    const byPairing = new Map<string, SessionContact[]>();
    let cancelled = false;
    void (async () => {
      for (const p of pairings) {
        if (!clients.current.has(p.pairingId)) {
          clients.current.set(p.pairingId, await PairingClient.create(db, p));
          if (cancelled) return;
          setClientsVersion((v) => v + 1); // a client is now reachable → wake the hooks that subscribe through it
        }
        if (cancelled) return;
        unsubs.push(
          clients.current.get(p.pairingId)!.subscribeSessions((list) => {
            byPairing.set(p.pairingId, list);
            const all = [...byPairing.values()].flat();
            setSessions(all.filter((s) => !s.archived));
            setArchivedSessions(all.filter((s) => s.archived));
            setSessionsReady(true);
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
      unsubs.forEach((u) => u?.());
    };
  }, [ready, pairings]);

  // Listener priority: once the structural list is ready and the browser is idle, flip the gate that lets the
  // bulk per-session listeners attach.
  useEffect(() => {
    if (!sessionsReady || secondaryReady) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (w.requestIdleCallback) {
      const h = w.requestIdleCallback(() => setSecondaryReady(true), { timeout: 1000 });
      return () => w.cancelIdleCallback?.(h);
    }
    const t = setTimeout(() => setSecondaryReady(true), 200);
    return () => clearTimeout(t);
  }, [sessionsReady, secondaryReady]);

  // Recent messages per session → the sidebar preview and unread count. Deferred until secondaryReady.
  const sessionKeys = sessions.map((s) => `${s.pairingId}/${s.sessionId}`).join(",");
  useEffect(() => {
    if (!secondaryReady) return;
    const unsubs: Array<() => void> = [];
    for (const s of sessions) {
      const key = `${s.pairingId}/${s.sessionId}`;
      const client = clients.current.get(s.pairingId);
      if (!client) continue;
      unsubs.push(
        client.subscribeChat(
          s.sessionId,
          (msgs) => {
            const last = msgs.filter((m) => !m.peer).at(-1);
            setPreviews((p) => ({
              ...p,
              [key]: last ? { text: last.att && !last.text ? "📷 Photo" : plainPreview(last.text), ts: last.ts } : { text: "", ts: 0 },
            }));
            setUnreads((u) => ({ ...u, [key]: unreadCount(msgs) }));
          },
          // Wide enough to count unread messages for the badge, not just show the latest — the same reason
          // the phone caps its overview listener rather than taking only the last message. Still bounded:
          // a badge saturating at the cap is fine, an unbounded listener on every session is not.
          UNREAD_SCAN,
        ),
      );
    }
    return () => unsubs.forEach((u) => u?.());
    // secondaryReady MUST be here: it gates the body, and sessions load before the idle callback flips it.
    // Without it the effect ran once while still gated and never again, so these listeners never attached
    // at all — no previews, no unread counts, and nothing to indicate they were missing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKeys, secondaryReady]);

  // Watch each archived session's main chat and unarchive it locally the moment an incoming (agent) message
  // lands after it was archived — so it doesn't sit invisible until the daily server reap.
  const archivedKeys = archivedSessions.map((s) => `${s.pairingId}/${s.sessionId}`).join(",");
  useEffect(() => {
    if (!secondaryReady) return;
    const unsubs: Array<() => void> = [];
    for (const s of archivedSessions) {
      const client = clients.current.get(s.pairingId);
      if (!client) continue;
      unsubs.push(
        client.subscribeChat(
          s.sessionId,
          (msgs) => {
            if (msgs.some((m) => m.role === "agent" && !m.peer && m.ts > s.archivedAt)) void client.unarchiveSession(s.sessionId);
          },
          5,
        ),
      );
    }
    return () => unsubs.forEach((u) => u?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedKeys, secondaryReady]);

  // Per-session to-do progress rings — rendered ONLY on the dashboard (`!selected`). Gate on that so opening a
  // chat tears down N listeners instead of holding them, and keep them out of the first-load burst.
  useEffect(() => {
    if (selected || !secondaryReady) return;
    const unsubs: Array<() => void> = [];
    for (const s of sessions) {
      const key = `${s.pairingId}/${s.sessionId}`;
      const client = clients.current.get(s.pairingId);
      if (!client) continue;
      unsubs.push(client.subscribeTodos(s.sessionId, (t) => setSessionTodos((m) => ({ ...m, [key]: t }))));
    }
    return () => unsubs.forEach((u) => u?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKeys, selected, secondaryReady]);

  return { sessions, archivedSessions, sessionsReady, clientsVersion, previews, unreads, sessionTodos };
}
